import { useEffect, useState } from "react";
import { Loader2, Phone, MapPin, Dog, Cat, Save, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ESTADOS, interesLabel, lugarLabel } from "@/components/closefriends/constants";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-CR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export const CloseFriendDetail = ({ id, onClose, onSaved }) => {
  const [c, setC] = useState(null);
  const [estado, setEstado] = useState("nuevo");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) { setC(null); return; }
    api.get(`/admin/close-friends/${id}`).then(({ data }) => { setC(data); setEstado(data.estado); setNotas(data.notas_internas || ""); });
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/close-friends/${id}`, { estado, notas_internas: notas });
      toast.success("Contacto actualizado"); onSaved();
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cf-detail" className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cream">
        {!c ? <div className="grid place-items-center py-16"><Loader2 className="w-7 h-7 animate-spin text-coral" /></div> : (
          <>
            <DialogHeader><DialogTitle data-testid="cf-detail-name" className="font-display text-2xl">{c.nombre}</DialogTitle></DialogHeader>
            <div className="flex flex-wrap gap-3 text-sm text-charcoal">
              <span className="inline-flex items-center gap-1"><Phone className="w-4 h-4 text-coral" />{c.telefono}</span>
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4 text-coral" />{c.zona}</span>
              <a data-testid="cf-detail-whatsapp" href={`https://wa.me/506${c.telefono}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-coral font-semibold"><MessageCircle className="w-4 h-4" />WhatsApp</a>
            </div>
            <p className="text-sm text-muted-moss">Interés en prueba Lenda: <b className="text-charcoal">{interesLabel(c.interes_prueba)}</b> · Origen: {c.origen} · Creado {fmt(c.fecha_creacion)}</p>

            <div className="rounded-2xl bg-white border border-[#E3E7E2] p-4 space-y-3">
              <label className="block text-sm font-semibold text-charcoal">Estado
                <select data-testid="cf-detail-estado" value={estado} onChange={(e) => setEstado(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-xl bg-white border border-[#E3E7E2] text-sm font-normal">
                  {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
              </label>
              <label className="block text-sm font-semibold text-charcoal">Notas internas
                <textarea data-testid="cf-detail-notas" rows={3} value={notas} onChange={(e) => setNotas(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E3E7E2] text-sm font-normal" />
              </label>
              <button data-testid="cf-detail-save" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
              </button>
            </div>

            <h3 className="font-bold text-charcoal">Mascotas y alimentos ({c.mascotas.length})</h3>
            <div className="space-y-3">
              {c.mascotas.map((m) => <PetBlock key={m.id} m={m} />)}
            </div>

            <h3 className="font-bold text-charcoal">Historial de respuestas ({c.respuestas.length})</h3>
            <div className="space-y-2">
              {c.respuestas.map((r) => (
                <div key={r.id} data-testid={`cf-detail-resp-${r.id}`} className="rounded-xl bg-white border border-[#E3E7E2] p-3 text-sm">
                  <p className="text-xs text-muted-moss">{fmt(r.fecha)} · {r.origen}</p>
                  <p className="text-charcoal">{r.observaciones || <span className="text-muted-moss">Sin observaciones</span>}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PetBlock = ({ m }) => (
  <div className="rounded-2xl bg-white border border-[#E3E7E2] p-4">
    <p className="font-semibold text-charcoal flex items-center gap-2">
      {m.tipo === "gato" ? <Cat className="w-4 h-4 text-coral" /> : <Dog className="w-4 h-4 text-coral" />}
      {m.nombre_mascota} <span className="text-xs text-muted-moss font-normal">({m.tipo}{m.cantidad > 1 ? ` × ${m.cantidad}` : ""}) · {fmt(m.fecha)}</span>
    </p>
    <div className="mt-2 space-y-2">
      {m.alimentos.map((a, i) => (
        <div key={i} className="rounded-xl bg-cream-2/70 p-3 text-sm">
          <p className="font-bold text-charcoal">{a.marca} {a.linea && <span className="font-normal">· {a.linea}</span>}</p>
          <p className="text-muted-moss text-xs mt-0.5">{a.kg} kg · {formatPrice(a.precio)} · {formatPrice(a.precio_por_kg)}/kg</p>
          <p className="text-muted-moss text-xs">{lugarLabel(a.lugar_compra)} ({a.lugar_nombre}) · {a.frecuencia} · {a.motivo}</p>
        </div>
      ))}
    </div>
  </div>
);
